import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { supabase } from './supabaseClient';

import './App.css';

import { DEMO_STATE } from './demoData';
import * as storage from './storageService';
import { generateCurriculum, getSortedUnlearnedRules } from './curriculumService';
import * as srsService from './srsService';

import BatchSummaryScreen from './components/BatchSummaryScreen';
import FeedbackModal from './components/FeedbackModal';
import ContactModal from './components/ContactModal';
import UpgradeModal from './components/UpgradeModal';
import DeNovoSetupModal from './components/DeNovoSetupModal';
import LoginScreen from './components/LoginScreen';
import InteractiveOnboarding from './components/InteractiveOnboarding';
import LearnModeScreen from './components/LearnModeScreen';
import TestModeScreen from './components/TestModeScreen';
import HistoryModal from './components/HistoryModal';
import Dashboard from './components/Dashboard';
import LibraryView from './components/LibraryView';
import PlannerView from './components/PlannerView';
import SettingsPage from './components/SettingsPage';
import DeNovoView from './components/DeNovoView';
import CorpusJurisView from './components/CorpusJurisView';
import SetupView from './components/SetupView';
import CurriculumConfigModal from './components/CurriculumConfigModal';
import ArenaView from './components/ArenaView';
import ProfileView from './components/ProfileView';
import NotificationDropdown from './components/NotificationDropdown';


import { calculateLocalStreak } from './utils/masteryUtils';


import { subjectMap, getLocalDateString, orderedSubjectKeys, SUBJECT_PALETTE } from './constants';


const MASTERY_INTERVAL_THRESHOLD = 21;

const calculateRuleStatus = (rule, userProgress) => {
  if (!userProgress) return 'not_started';

  const srsData = userProgress.srs_data || {};
  const hasStartedLearning = srsData.learningStepIndex !== undefined;
  const isGraduated = srsData.learningStepIndex === -1;
  const interval = srsData.currentInterval || 0;

  let srsStatus = 'not_started';
  if (hasStartedLearning && !isGraduated) srsStatus = 'in_progress';
  else if (isGraduated && interval < MASTERY_INTERVAL_THRESHOLD) srsStatus = 'in_progress';
  else if (isGraduated && interval >= MASTERY_INTERVAL_THRESHOLD) srsStatus = 'srs_mastered';

  if (srsStatus === 'srs_mastered') {
    if (rule.isHFR) {
      const attempts = userProgress.attempt_history || [];
      const hasPassedQuiz = attempts.some(a => a.source === 'quiz' && a.userScore >= 2);
      if (!hasPassedQuiz) return 'in_progress';
    }
    return 'mastered';
  }

  return srsStatus;
};

const getMajorUnit = (hnu) => {
  if (!hnu) return 9999;
  const parts = hnu.split('.');
  const val = parseInt(parts[0], 10);
  return isNaN(val) ? 9999 : val;
};

const ViewSwitcherDropdown = ({ currentView, setView, views, viewNamesMap, isScrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toggleDropdown = () => setIsOpen(prev => !prev);
  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
  }, []);
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);
  const handleSelectView = (viewKey) => { setView(viewKey); setIsOpen(false); };
  const currentViewName = viewNamesMap[currentView] || currentView;
  return (
    <div className={`view-switcher-dropdown fixed-header-element ${isScrolled ? 'scrolled' : ''} ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
      <button className="dropdown-trigger" onClick={toggleDropdown}>{currentViewName} {isOpen ? '▴' : '▾'}</button>
      {isOpen && <ul className="dropdown-menu">{views.map(viewKey => <li key={viewKey}><button onClick={() => handleSelectView(viewKey)} className={currentView === viewKey ? 'active' : ''}>{viewNamesMap[viewKey] || viewKey}</button></li>)}</ul>}
    </div>
  );
};

const viewIcons = { dashboard: 'dashboard', library: 'library_books', corpus: 'gavel', deNovo: 'psychology', planner: 'calendar_month', settings: 'settings' };

function App() {
  const [session, setSession] = useState(null);
  const [appState, setAppState] = useState('loading');
  const [canonicalData, setCanonicalData] = useState({ rules: [], quizzes: [] });
  const [userData, setUserData] = useState(null);
   const [cramDecks, setCramDecks] = useState([]);

  const [isDeNovoSetupModalOpen, setIsDeNovoSetupModalOpen] = useState(false);
  const userAccessLevel = userData?.access_level || 'free';

  const [userMeta, setUserMeta] = useState(null);
  const [subject, setSubject] = useState('torts');
  const [view, setView] = useState('dashboard');
  const [mode, setMode] = useState('list');
  const [currentRule, setCurrentRule] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [historyModalRule, setHistoryModalRule] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [, setIsAddingRule] = useState(false);
  const [deNovoSession, setDeNovoSession] = useState({ state: 'home', queue: [], currentIndex: 0, settings: null, history: [], elapsedTime: 0, isPaused: false, streak: 0, isRevealed: false, forcedQueue: null });

  const [isCurriculumConfigModalOpen, setIsCurriculumConfigModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackRuleId, setFeedbackRuleId] = useState(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const [apiPort, setApiPort] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const mobileBreakpoint = 900;
  const [isScrolled, setIsScrolled] = useState(false);
  const nodeRef = useRef(null);
  const [sessionQueue, setSessionQueue] = useState([]);
  const [sessionType, setSessionType] = useState(null);
  const [sessionProgress, setSessionProgress] = useState({ current: 0, total: 0 });
  const [showTutorial, setShowTutorial] = useState(false);

  const [completedBatchItems, setCompletedBatchItems] = useState([]);

  const lastProfileUpdateRef = useRef(0);
  const lastGoalsUpdateRef = useRef(0);
  const lastDocketUpdateRef = useRef(0);

  const userId = session?.user?.id;
  const prevAccessLevelRef = useRef(userData?.access_level);
  const [pointsToast, setPointsToast] = useState(null);

  const userDataRef = useRef(userData);
  
  const initializedUserRef = useRef(null);

  useEffect(() => {
    userDataRef.current = userData;
}, [userData]);

const triggerPointsToast = (amount) => {
      if (userData?.profile?.zen_mode) return;
      
      // Update Local State for immediate UI feedback
      setUserData(prev => ({
          ...prev,
          profile: {
              ...prev.profile,
              weekly_points: (prev.profile?.weekly_points || 0) + amount
          }
      }));

      setPointsToast(amount);
      setTimeout(() => setPointsToast(null), 2000);
  };

    const processedItemsMap = useMemo(() => {
    const map = new Map();
    const ruleIdToNameMap = new Map();
    const hnuToRuleMap = new Map();
    const rawHnuToRuleMap = new Map();

    const isPaidUser = userAccessLevel === 'cycle' || userAccessLevel === 'lifetime';

    canonicalData.rules.forEach(rule => {
      const hnu = rule.HNU || rule.hnu || '';
      const isRestricted = !isPaidUser && !rule.is_free_preview;

      map.set(rule.id, {
        ...rule,
        // Redact content if restricted, preserve metadata for structure
        content_html: isRestricted ? "This content is restricted. Please upgrade to access. Try [Intentional Torts] and [Negligence] in the Torts subject for free!" : rule.content_html,
        content_plain: isRestricted ? "This content is restricted. Please upgrade to access. Try [Intentional Torts] and [Negligence] in the Torts subject for free!" : rule.content_plain,
        itemType: 'rule',
        sortingHNU: hnu,
        hnuDepth: hnu ? hnu.split('.').length : 0,
        isHFR: rule.isHFR || rule.is_hfr
      });
      
      ruleIdToNameMap.set(rule.id, rule.ruleName);
      if (hnu) {
        const strictKey = `${rule.subject}-${hnu}`.toLowerCase().trim();
        hnuToRuleMap.set(strictKey, rule);
        const rawKey = hnu.trim();
        if (!rawHnuToRuleMap.has(rawKey)) { rawHnuToRuleMap.set(rawKey, []); }
        rawHnuToRuleMap.get(rawKey).push(rule);
      }
    });

    canonicalData.quizzes.forEach(quiz => {
      let ruleName = "Quiz: [Unknown Rule]";
      let relatedRuleIds = [];
      let sortingHNU = '';
      let displayPath = [];
      let subject = 'torts';
      let hnuDepth = 0;

      if (quiz.related_HNU && quiz.related_HNU.length > 0) {
        const primaryHNUString = quiz.related_HNU[quiz.related_HNU.length - 1];
        const parts = primaryHNUString.split('-');
        const rawHnu = parts.length > 1 ? parts[parts.length - 1] : parts[0];
        sortingHNU = rawHnu;
        hnuDepth = sortingHNU ? sortingHNU.split('.').length : 0;

        relatedRuleIds = quiz.related_HNU.map(quizHnuString => {
          const normalizedHnuString = String(quizHnuString).toLowerCase().trim();
          let foundRule = hnuToRuleMap.get(normalizedHnuString);
          if (!foundRule) {
            const p = normalizedHnuString.split('-');
            const raw = p.length > 1 ? p.slice(1).join('-') : p[0];
            const potentialRules = rawHnuToRuleMap.get(raw);
            if (potentialRules && potentialRules.length > 0) {
              foundRule = potentialRules.find(r => normalizedHnuString.includes(r.subject.toLowerCase()));
              if (!foundRule) foundRule = potentialRules[0];
            }
          }
          return foundRule ? foundRule.id : null;
        }).filter(Boolean);

        if (relatedRuleIds.length > 0) {
          const primaryRuleId = relatedRuleIds[relatedRuleIds.length - 1];
          const primaryRule = map.get(primaryRuleId);
          if (primaryRule) {
            ruleName = `Quiz: ${primaryRule.ruleName}`;
            displayPath = primaryRule.displayPath;
            subject = primaryRule.subject;
            if (!sortingHNU) sortingHNU = primaryRule.HNU;
            hnuDepth = primaryRule.hnuDepth || 0;
          }
        }
      }

      const isRestricted = !isPaidUser && !quiz.is_free_preview;

      map.set(quiz.fact_pattern_id, {
        ...quiz,
        fact_pattern: isRestricted ? "This content is restricted. Please upgrade to access. Try Intentional Torts and Negligence for free!" : quiz.fact_pattern,
        answer_key: isRestricted ? "This content is restricted. Please upgrade to access. Try Intentional Torts and Negligence for free!" : quiz.answer_key,
        id: quiz.fact_pattern_id,
        itemType: 'quiz',
        ruleName,
        related_rule_ids: relatedRuleIds,
        displayPath,
        subject,
        sortingHNU,
        hnuDepth
      });
    });

    return map;
  }, [canonicalData, userAccessLevel]);

  const allCombinedItems = useMemo(() => {
    if (appState !== 'ready' || !userData) return new Map();
    const combinedMap = new Map();

    processedItemsMap.forEach((item, id) => {
      if (item.itemType === 'rule') {
        const userProgress = (userData.rulesProgress || {})[id] || {};
        const customData = userProgress.custom_data || {}; // Fetch user overrides
        const derivedStatus = calculateRuleStatus(item, userProgress);

        combinedMap.set(id, {
          ...item,
          ...customData, // Apply user overrides
          status: derivedStatus,
          isFlagged: userProgress.is_flagged !== undefined ? userProgress.is_flagged : item.isFlagged,
          keynote: userProgress.keynote,
          attempt_history: userProgress.attempt_history || [],
          srs_data: userProgress.srs_data
        });
      } else if (item.itemType === 'quiz') {
        const quizProgress = (userData.quizzesProgress || {})[id] || {};
        const quizStatus = quizProgress.status || 'not_started';

        combinedMap.set(id, {
          ...item,
          status: quizStatus,
          isFlagged: false,
          isHFR: false,
          attempt_history: quizProgress.attempt_history || [],
          high_score: quizProgress.high_score || 0
        });
      }
    });
    return combinedMap;
  }, [appState, userData, processedItemsMap]);


  useEffect(() => {
    const currentAccess = userData?.access_level;
    const wasFree = prevAccessLevelRef.current === 'free';
    const isNowPaid = currentAccess === 'cycle' || currentAccess === 'lifetime';

    if (wasFree && isNowPaid && userData?.userMeta?.is_demo_active) {
        console.log("Upgrade detected! Starting fresh study plan...");
        handleExitDemoMode(true); // skipConfirm = true
    }
  
    prevAccessLevelRef.current = currentAccess;
  }, [userData?.access_level, userData?.userMeta?.is_demo_active]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    orderedSubjectKeys.forEach((key, index) => {
      root.style.setProperty(`--subj-${key}`, SUBJECT_PALETTE[index]);
    });
  }, []);

  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setHeaderVisible(false);
      } else {
        setHeaderVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const handleUpdateUserData = useCallback((updatedDataChunk) => {
    if (!userId) return;
    if (updatedDataChunk.profile) {
        lastProfileUpdateRef.current = Date.now();
    }
    setUserData(prev => {
      const deeplyMergedData = {
        ...prev,
        ...updatedDataChunk,
        access_level: prev.access_level, // Preserve current access level from state
        profile: {
          ...(prev.profile || {}),
          ...(updatedDataChunk.profile || {}),
          preferences: { ...(prev.profile?.preferences || {}), ...(updatedDataChunk.profile?.preferences || {}) },
          llm_settings: { ...(prev.profile?.llm_settings || {}), ...(updatedDataChunk.profile?.llm_settings || {}) }
        },
        goals: { ...(prev.goals || {}), ...(updatedDataChunk.goals || {}) },
        diagnostics: { ...(prev.diagnostics || {}), ...(updatedDataChunk.diagnostics || {}) },
        rulesProgress: updatedDataChunk.rulesProgress ? updatedDataChunk.rulesProgress : prev.rulesProgress,
        quizzesProgress: updatedDataChunk.quizzesProgress ? updatedDataChunk.quizzesProgress : (prev.quizzesProgress || {}),
        docket: updatedDataChunk.docket ? updatedDataChunk.docket : prev.docket,
        userStats: updatedDataChunk.userStats ? updatedDataChunk.userStats : prev.userStats,
      };

      storage.saveUserData(userId, deeplyMergedData);
      return deeplyMergedData;
    });
  }, [userId]);

  const handleDismissHint = useCallback((hintId) => {
    if (!userId || !userData?.userMeta) return;
    const currentSeen = userData.userMeta.seen_hints || [];
    if (currentSeen.includes(hintId)) return;

    const updatedMeta = {
      ...userData.userMeta,
      seen_hints: [...currentSeen, hintId]
    };

    // Update local state and persist to DB
    setUserData(prev => ({ ...prev, userMeta: updatedMeta }));
    storage.saveUserMeta(userId, updatedMeta);
  }, [userId, userData, setUserData]);



const handleCardUpdate = useCallback(async (ruleId, updatedDataChunk) => {
    if (!userId) return;
    
    // Update Local State (Optimistic)
    setUserData(prev => {
      const oldData = prev.rulesProgress[ruleId];
      const wasNew = !oldData?.srs_data?.lastReviewedDate;
      const isNowStarted = !!updatedDataChunk.srs_data?.lastReviewedDate;
      let newDocket = prev.docket;
      let docketChanged = false;

      // Handle "First Time Learned" logic for Docket
      if (wasNew && isNowStarted) {
        newDocket = JSON.parse(JSON.stringify(prev.docket || { schedule: [] }));
        const todayStr = getLocalDateString(new Date());
        let dayEntry = newDocket.schedule.find(d => d.date === todayStr);
        if (!dayEntry) {
          dayEntry = { date: todayStr, metrics: { learned_count: 0, quiz_count: 0 } };
          newDocket.schedule.push(dayEntry);
        }
        if (!dayEntry.metrics) dayEntry.metrics = { learned_count: 0, quiz_count: 0 };
        dayEntry.metrics.learned_count = (dayEntry.metrics.learned_count || 0) + 1;
        docketChanged = true;
      }

      const newRulesProgress = { ...prev.rulesProgress, [ruleId]: updatedDataChunk };
      
      // Async Save to DB (Granular)
      storage.saveRuleProgress(userId, ruleId, updatedDataChunk).then(async () => {
          if (docketChanged) storage.saveDocket(userId, newDocket);
          
          const rule = allCombinedItems.get(ruleId);
          if (rule && !userData?.profile?.zen_mode) {
              const achievement = await storage.checkAndUnlockChapterAchievement(userId, rule, allCombinedItems, newRulesProgress);
              
              if (achievement?.unlocked) {
                  // Trigger Special Badge Toast
                  setPointsToast({ type: 'badge', name: achievement.name });
                  setTimeout(() => setPointsToast(null), 4000);
              }
          }
      });

      return { ...prev, rulesProgress: newRulesProgress, docket: newDocket };
    });
}, [userId, allCombinedItems]); // Added allCombinedItems dependency


  // --- INITIALIZATION LOGIC ---
  useEffect(() => {
    if (!session || !userId) {
        initializedUserRef.current = null;
        return;
    }
    if (initializedUserRef.current === userId) return;

    let stateSubscription;

    const initializeApp = async () => {
      setAppState('loading');
      setApiPort(5001);
      
      // 1. NUCLEAR CLEANUP: Remove any lingering channels to prevent "Zombie Listeners"
      // This fixes the "current.profile" error persisting from old code versions.
      supabase.getChannels().forEach(channel => {
          console.log("Cleaning up stale channel:", channel.topic);
          supabase.removeChannel(channel);
      });

      try {
        initializedUserRef.current = userId;

        const [canonData, uData] = await Promise.all([
          storage.loadCanonicalData(),
          storage.loadFullUserState(userId) 
        ]);

        let finalUserData = uData;

        if (uData.onboarding_status === 'initiated') {
            setCanonicalData(canonData);
            setUserData(uData); 
            setAppState('setup');
            return; 
        }

        if (uData.onboarding_status === 'completed' || uData.userMeta.onboarding_complete) {
            console.log("Onboarding previously completed. Proceeding to main app flow.");
        }

        const hasStartedRules = Object.keys(uData.rulesProgress || {}).length > 0;
        const isFreeUser = uData.access_level === 'free';
        
        if (!hasStartedRules && isFreeUser && !uData.userMeta?.is_demo_active) {
          console.log("Initializing Demo Mode for Onboarded Free User...");
          finalUserData = {
            ...uData,
            ...DEMO_STATE,
            userMeta: { ...uData.userMeta, ...DEMO_STATE.userMeta, onboarding_complete: true }
          };
          storage.saveUserData(userId, finalUserData);
        }
        
        const todayStr = getLocalDateString(new Date());

        const currentSchedule = Array.isArray(finalUserData.docket?.schedule) ? finalUserData.docket.schedule : [];
        const docketExists = currentSchedule.some(d => d.date === todayStr);

        if (!docketExists) {
          const { schedule } = generateCurriculum({ userData: finalUserData, canonicalData: canonData });
          finalUserData = { ...finalUserData, docket: { ...(finalUserData.docket || {}), schedule } };
          storage.saveDocket(userId, finalUserData.docket);
        }

        setCanonicalData(canonData);
        setUserData(finalUserData);
        setCramDecks(finalUserData.cramDecks || []);
        setUserMeta(finalUserData.userMeta);

        setAppState('ready');

        // 2. CREATE NEW SUBSCRIPTION WITH UNIQUE ID
        stateSubscription = supabase
          .channel(`app-sync-${userId}-${Date.now()}`) // Unique channel ID

          // A. PROFILE/META LISTENER (Refined)
          .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'users',
            filter: `id=eq.${userId}`
          }, (payload) => {
            if (Date.now() - lastProfileUpdateRef.current < 2000) return;

            // Use functional update to guarantee latest state ('prev')
            setUserData(prev => {
                if (!prev) return prev; // Safety guard

                console.log("Syncing User/Meta...", payload.new.meta);

                // Safe access to nested objects with fallbacks
                const oldProfile = prev.profile || {};
                const incomingMeta = payload.new.meta || prev.userMeta || {};
                
                const newProfile = {
                    ...oldProfile,
                    name: payload.new.name || oldProfile.name,
                    phone: payload.new.phone || oldProfile.phone,
                    display_name: payload.new.display_name || oldProfile.display_name,
                    zen_mode: payload.new.zen_mode !== undefined ? payload.new.zen_mode : oldProfile.zen_mode,
                    weekly_points: payload.new.weekly_points !== undefined ? payload.new.weekly_points : oldProfile.weekly_points,
                    preferences: payload.new.preferences || oldProfile.preferences || {}
                };

                return {
                    ...prev,
                    access_level: payload.new.access_level || prev.access_level,
                    userMeta: incomingMeta,
                    profile: newProfile
                };
            });
          })

          // B. DOCKET LISTENER
          .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_docket',
            filter: `user_id=eq.${userId}`
          }, (payload) => {
            if (Date.now() - lastDocketUpdateRef.current < 2000) return;
            setUserData(prev => (prev ? { ...prev, docket: payload.new } : prev));
          })

          // C. GOALS LISTENER
          .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_goals',
            filter: `user_id=eq.${userId}`
          }, (payload) => {
            if (Date.now() - lastGoalsUpdateRef.current < 2000) return;

            const newGoals = {
              mix_mode: payload.new.mix_mode,
              daily_rule_target: payload.new.daily_target,
              target_mastery_date: payload.new.target_mastery_date,
              bar_exam_date: payload.new.exam_date,
              study_days: payload.new.study_days,
              excluded_subjects: payload.new.excluded_subjects,
              denovo_track_subjects: payload.new.denovo_track_subjects,
              hfr_restricted_subjects: payload.new.hfr_restricted_subjects
            };

            setUserData(prev => {
                if (!prev) return prev;
                const updatedUserData = { ...prev, goals: newGoals };
                recalculateCurriculum(updatedUserData);
                return updatedUserData;
            });
          })
          .subscribe();

      } catch (error) { console.error('CRITICAL ERROR during initialization:', error); }
    };

    initializeApp();

    return () => {
      if (stateSubscription) supabase.removeChannel(stateSubscription);
    };
}, [session, userId])

useEffect(() => {
    // Only sync if we have a valid docket with schedule items
    if (appState === 'ready' && userId && userData?.docket?.schedule?.length > 0) {
        const streak = calculateLocalStreak(userData.docket);
        // Only sync if streak is actually > 0 to avoid overwriting existing data with 0 during load
        if (streak > 0) {
            storage.syncStreakToDb(userId, streak);
        }
    }
  }, [appState, userId, userData?.docket?.schedule]);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < mobileBreakpoint);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- DATA PROCESSING ---

  const allItemsBySubject = useMemo(() => {
    if (allCombinedItems.size === 0) return {};
    const hierarchy = {};
    const subjectKeys = Object.keys(subjectMap);
    subjectKeys.forEach(key => hierarchy[key.toLowerCase()] = []);

    allCombinedItems.forEach(item => {
      let targetKey = item.subject.toLowerCase();
      if (hierarchy[targetKey]) hierarchy[targetKey].push(item);
    });

    Object.keys(hierarchy).forEach(key => {
      hierarchy[key].sort((a, b) => {
        const hnuA = a.sortingHNU || a.HNU || '';
        const hnuB = b.sortingHNU || b.HNU || '';
        const cmp = hnuA.localeCompare(hnuB, undefined, { numeric: true });
        if (cmp === 0) {
          if (a.itemType === 'rule' && b.itemType === 'quiz') return -1;
          if (a.itemType === 'quiz' && b.itemType === 'rule') return 1;
          return a.id.localeCompare(b.id);
        }
        return cmp;
      });
    });
    return hierarchy;
  }, [allCombinedItems, subjectMap]);

  const subjectMapDerived = subjectMap;

  const todaysDocketEntry = useMemo(() => {
    if (!userData?.docket?.schedule || !Array.isArray(userData.docket.schedule)) return null;
    return userData.docket.schedule.find(day => day.date === getLocalDateString(new Date()));
  }, [userData?.docket]);

  const rules = useMemo(() => allItemsBySubject[subject.toLowerCase()] || [], [allItemsBySubject, subject]);

  const reviewQueueStats = useMemo(() => {
    if (!userData?.rulesProgress || !todaysDocketEntry) return { reviewCount: 0, fastTrackCount: 0 };
    
    // Get prescribed new Fast Track items for today
    const fastTrackQueue = todaysDocketEntry?.queues?.fast_track || [];
    const unlearnedFastTrackCount = fastTrackQueue.filter(id => !userData.rulesProgress[id]?.srs_data?.lastReviewedDate).length;

    // Get count of only the review items
    const reviewQueueForCount = srsService.buildSessionQueue(
      allItemsBySubject,
      userData.rulesProgress,
      { subjects: ['all'], limit: 999, sessionMode: 'daily_review' },
      userAccessLevel,
      userData.goals,
      0, // completedNewCount, not relevant for pure review count
      [] // Pass empty fast track queue to isolate review items
    );
    
    return { reviewCount: reviewQueueForCount.length, fastTrackCount: unlearnedFastTrackCount };
  }, [userData, allItemsBySubject, userAccessLevel, todaysDocketEntry]);

  // Extract count for legacy props
  const reviewCount = reviewQueueStats.count;

const generateLearnQueue = useCallback((limit = 5) => {
    const queueItems = getSortedUnlearnedRules({ 
        userData, 
        allItemsMap: allCombinedItems 
    });
    return queueItems.slice(0, limit).map(r => r.id);
  }, [allCombinedItems, userData]);


  const handleUpdateRuleKeynote = useCallback((ruleId, newKeynote) => {
    if (!userId) return;
    
    // 1. Update Local State
    setUserData(prev => {
      const newRulesProgress = { ...prev.rulesProgress };
      if (!newRulesProgress[ruleId]) newRulesProgress[ruleId] = {};
      newRulesProgress[ruleId].keynote = newKeynote;
      return { ...prev, rulesProgress: newRulesProgress };
    });

    // 2. Granular DB Update
    storage.saveRuleProgress(userId, ruleId, { keynote: newKeynote });
  }, [userId]);

  const handleContinueNextBatch = () => {
    const nextQueue = generateLearnQueue(5);
    if (nextQueue.length === 0) {
      alert("No more new rules available right now!");
      handleBackToList();
      return;
    }
    setSessionQueue(nextQueue);
    setSessionType('LEARN_NEW_RULES'); // Explicitly set type for the next batch
    setCompletedBatchItems([]);
    setSessionProgress({ current: 0, total: nextQueue.length });
    const firstId = nextQueue[0];
    setCurrentRule(allCombinedItems.get(firstId));
    setCurrentQuiz(null);
    setMode('learn');
  };

  const handleStartDynamicSession = useCallback((typeOrPhase) => {
    let queue = [];
    let sessionKey = '';
    const SESSION_SAFETY_CAP = 200;
    const isDeNovo = typeOrPhase === 'REVIEW';

    if (isDeNovo) {
      sessionKey = 'DE_NOVO_REVIEW';
      const fastTrackQueue = todaysDocketEntry?.queues?.fast_track || [];
      queue = srsService.buildSessionQueue(
        allItemsBySubject,
        userData.rulesProgress || {},
        { subjects: ['all'], limit: SESSION_SAFETY_CAP, sessionMode: 'daily_review' },
        userAccessLevel,
        userData.goals,
        0, // completed count not needed here, queue builder filters
        fastTrackQueue
      );
    }
    else if (typeOrPhase === 'LEARN') {
      sessionKey = 'LEARN_NEW_RULES';
      const learnQueue = todaysDocketEntry?.queues?.learn || [];
      queue = learnQueue
        .filter(id => !(userData.rulesProgress[id]?.srs_data?.lastReviewedDate))
        .slice(0, 5);
      setCompletedBatchItems([]);
    }
    else if (typeOrPhase === 'QUIZ') {
      sessionKey = 'PRACTICE_QUIZZES';
      let potential = [...allCombinedItems.values()].filter(i => {
        if (i.itemType !== 'quiz') return false;
        if (userAccessLevel === 'free' && !i.is_free_preview) return false;
        if (i.status === 'mastered') return false;
        const parents = i.related_rule_ids || [];
        return parents.length > 0 && parents.every(rid => {
          const r = allCombinedItems.get(rid);
          return r && r.status !== 'not_started';
        });
      });
      queue = potential.sort(() => 0.5 - Math.random()).slice(0, 3).map(q => q.id);
    }

    if (queue.length === 0) {
      alert("No items available for this session type right now! Press the refresh button to reload your study plan.");
      return;
    }

    setSessionQueue(queue);
    setSessionType(sessionKey);
    setSessionProgress({ current: 0, total: queue.length });
    const firstId = queue[0];
    const item = allCombinedItems.get(firstId);

    if (isDeNovo) {
      setDeNovoSession({ state: 'active', queue, currentIndex: 0, history: [], elapsedTime: 0, streak: 0, isPaused: false, isRevealed: false });
      setView('deNovo');
    } else if (typeOrPhase === 'QUIZ') {
      setCurrentQuiz(item);
      setCurrentRule(null);
      setMode('test');
    } else {
      setCurrentRule(item);
      setCurrentQuiz(null);
      setMode('learn');
    }
  }, [allCombinedItems, userData, userAccessLevel, allItemsBySubject, todaysDocketEntry]);

  const recalculateCurriculum = useCallback(async (manualUserData = null) => {
    if (!userId) return;
    lastDocketUpdateRef.current = Date.now();

    const baseData = manualUserData || userData;
    if (!baseData) return;

    const { schedule } = generateCurriculum({ userData: baseData, canonicalData });
    const finalDocket = { ...(baseData.docket || {}), schedule };
    const finalUserData = { ...baseData, docket: finalDocket };
    
    setUserData(finalUserData);
    await storage.saveDocket(userId, finalDocket); // Granular save
}, [userId, userData, canonicalData]);


  const handleRecalculateCurriculum = useCallback(async () => {
    if (!userId) return;
    
    lastDocketUpdateRef.current = Date.now();
    lastGoalsUpdateRef.current = Date.now();
    // 1. Fetch the absolute latest state from Supabase 
    // (This captures work done on iOS or other tabs)
    const freshUserData = await storage.loadFullUserState(userId);
    await recalculateCurriculum(freshUserData);
  }, [userId, recalculateCurriculum]);

  const userProfile = useMemo(() => userData?.profile || {}, [userData]);
  const userGoals = useMemo(() => userData?.goals || {}, [userData]);
  const llmSettings = useMemo(() => userProfile?.llm_settings || {}, [userProfile]);
  const isDarkMode = useMemo(() => userProfile?.preferences?.default_theme === 'dark', [userProfile]);

  useEffect(() => { document.body.classList.toggle('dark-mode', isDarkMode); }, [isDarkMode]);

  const handleToggleDarkMode = useCallback(() => handleUpdateUserData({ profile: { ...userProfile, preferences: { ...(userProfile.preferences || {}), default_theme: !isDarkMode ? 'dark' : 'light' } } }), [isDarkMode, userProfile, handleUpdateUserData]);

  useEffect(() => {
  if (appState !== 'ready' || !userData || !todaysDocketEntry || !userId) return;

  const targets = todaysDocketEntry.targets || {};
  const metrics = todaysDocketEntry.metrics || {};
  const learnDone = targets.learn === 0 || metrics.learned_count >= targets.learn;
  const quizDone = targets.quiz === 0 || metrics.quiz_count >= targets.quiz;

}, [userData, todaysDocketEntry, userMeta, appState, userId, reviewCount]);

  useEffect(() => {
    let timer;
    if (deNovoSession.state === 'active' && !deNovoSession.isPaused) {
      timer = setInterval(() => {
        setDeNovoSession(prev => ({ ...prev, elapsedTime: prev.elapsedTime + 1 }));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [deNovoSession.state, deNovoSession.isPaused]);

  useEffect(() => {
    if (deNovoSession.state === 'active') document.body.classList.add('no-scroll');
    else document.body.classList.remove('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, [deNovoSession.state]);

  const subjectProgress = useMemo(() => {
    const subjectItems = rules.filter(item => item.itemType === 'rule');
    if (!subjectItems.length) return { mastered: 0, inProgress: 0, total: 0 };
    const total = subjectItems.length;
    const mastered = subjectItems.filter(r => r.status === 'mastered').length;
    const inProgress = subjectItems.filter(r => r.status === 'in_progress').length;
    return { mastered, inProgress, total };
  }, [rules]);

  const chapterModeEligibility = useMemo(() => {
    const subjectRules = rules.filter(item => item.itemType === 'rule');
    return { learnable: subjectRules.filter(r => r.status === 'not_started').length, refinable: subjectRules.filter(r => r.status === 'in_progress').length };
  }, [rules]);

  const flaggedForReviewCount = useMemo(() => [...allCombinedItems.values()].filter(r => r.isFlagged).length, [allCombinedItems]);

  const handleCompleteLearn = (ruleId) => {
    const rule = allCombinedItems.get(ruleId);
    if (rule && (!rule.status || rule.status === 'not_started')) {
      // 1. Prepare SRS Data
      const initialSrsData = {
          learningStepIndex: 0,
          easeFactor: 2.5,
          currentInterval: 0,
          nextDueDate: new Date().toISOString(),
          repetitions: 0,
          lapses: 0
      };

      // 2. Granular DB Update
      if (userId) {
          storage.saveRuleProgress(userId, ruleId, { 
              status: 'in_progress', 
              srs_data: initialSrsData 
          });
      }

      // 3. Update Local State
      setUserData(prev => {
        const newRulesProgress = { ...prev.rulesProgress };
        if (!newRulesProgress[ruleId]) newRulesProgress[ruleId] = {};
        newRulesProgress[ruleId].srs_data = initialSrsData;
        newRulesProgress[ruleId].status = 'in_progress';
        return { ...prev, rulesProgress: newRulesProgress };
      });
    }
  };

  const handleCompleteTest = (itemId, score, userAttempt, feedback) => {
    const item = allCombinedItems.get(itemId);
    if (!item) return;

    // 1. Granular DB Update (Log the attempt)
    if (userId) {
        storage.saveQuizAttempt(userId, {
            quiz_id: itemId,
            score,
            user_answer: userAttempt,
            feedback
        });
    }

    // 2. Update Local State (Quizzes & Rules Linked History)
    setUserData(prev => {
        const newRulesProgress = { ...prev.rulesProgress };
        const newQuizzesProgress = { ...prev.quizzesProgress };
        const timestamp = new Date().toISOString();

        // A. Update Linked Rules (Legacy History Support)
        let ruleIdsToUpdate = [];
        if (item.itemType === 'quiz' && item.related_rule_ids) ruleIdsToUpdate = item.related_rule_ids;
        else if (item.itemType === 'rule') ruleIdsToUpdate = [item.id];

        ruleIdsToUpdate.forEach(ruleId => {
            const newAttempt = { timestamp, userScore: score, userAttempt, feedback, source: 'quiz', sourceId: itemId };
            const existingData = newRulesProgress[ruleId] || {};
            newRulesProgress[ruleId] = { 
                ...existingData, 
                attempt_history: [newAttempt, ...(existingData.attempt_history || [])] 
            };
        });

        // B. Update Quiz Status & History
        if (item.itemType === 'quiz') {
            const quizAttempt = { timestamp, userScore: score, userAttempt, feedback };
            const existingQuizData = newQuizzesProgress[itemId] || {};
            const isMastered = score >= 3 || existingQuizData.status === 'mastered';
            
            newQuizzesProgress[itemId] = { 
                ...existingQuizData, 
                status: isMastered ? 'mastered' : 'in_progress', 
                attempt_history: [quizAttempt, ...(existingQuizData.attempt_history || [])], 
                last_attempt: timestamp, 
                high_score: Math.max(score, existingQuizData.high_score || 0) 
            };
        }

        return { ...prev, rulesProgress: newRulesProgress, quizzesProgress: newQuizzesProgress };
    });

    if (userId) {
        storage.logActivity(userId, 'finish_quiz', itemId, score, {
            subject: item.subject // Assuming item has subject
        });
    }
  };

  const handleBackToList = () => {
    setCurrentRule(null);
    setCurrentQuiz(null);
    setMode('list');
  };

const startNextInSessionQueue = useCallback((queue, newSessionType) => {
    const isAdvancing = !queue;
    const currentQueue = isAdvancing ? sessionQueue.slice(1) : queue;
    const typeToUse = newSessionType || sessionType;

    if (isAdvancing) {
      if ((typeToUse === 'LEARN_NEW_RULES' || typeToUse === 'MORNING_DE_NOVO') && currentRule) {
        setCompletedBatchItems(prev => [...prev, currentRule.id]);
      }
      setSessionProgress(prev => ({ ...prev, current: prev.current + 1 }));
    }

    setSessionQueue(currentQueue);

    if (currentQueue && currentQueue.length > 0) {
      const nextId = currentQueue[0];
      const item = allCombinedItems.get(nextId);

      if (item) {
        if (typeToUse === 'LEARN_NEW_RULES' || typeToUse === 'MORNING_DE_NOVO') {
          setCurrentRule(item);
          setCurrentQuiz(null);
          setMode('learn');
        } else if (typeToUse === 'REFINE_CHAPTER' || typeToUse === 'REFINE_HFRS') {
          setCurrentRule(item);
          setCurrentQuiz(null);
          setMode('refine');
        } else if (typeToUse === 'PRACTICE_QUIZZES') {
          setCurrentRule(null);
          setCurrentQuiz(item);
          setMode('test');
        }
      } else {
        startNextInSessionQueue(currentQueue.slice(1), typeToUse);
      }
    } else {
      if (typeToUse === 'LEARN_NEW_RULES' || typeToUse === 'MORNING_DE_NOVO') {
        setMode('batch_summary');
        setCurrentRule(null);
      } else {
        handleBackToList();
      }
    }
  }, [sessionQueue, sessionType, allCombinedItems, currentRule, handleBackToList]);

  const handleDeNovoSessionEnd = useCallback((completedSessionType, sessionProgressData, sessionState, difficultRuleIds = [], isSessionFinished = false) => {
    if (!userId) return;

    if (sessionState.cramDeckId && completedSessionType === 'CRAM') {
      const deck = cramDecks.find(d => d.id === sessionState.cramDeckId);
      if (deck) {
        const originalCompleted = new Set(deck.progress?.completed_ids || []);
        sessionState.history.forEach(h => originalCompleted.add(h.id));
        
        const newProgress = { completed_ids: Array.from(originalCompleted) };
        storage.updateCramDeckProgress(deck.id, newProgress, true);
        
        setCramDecks(prev => prev.map(d => d.id === deck.id ? { ...d, progress: newProgress, last_studied_at: new Date().toISOString() } : d));
      }
    } else {
      // --- Original SRS Logic for non-cram sessions ---
      const enrichedProgressData = {};
      Object.entries(sessionProgressData).forEach(([ruleId, data]) => {
          const rule = allCombinedItems.get(ruleId);
          const derivedStatus = rule ? calculateRuleStatus(rule, data) : (data.status || 'in_progress');
          enrichedProgressData[ruleId] = { ...data, status: derivedStatus };
      });

      let newRulesLearnedCount = 0;
      Object.keys(enrichedProgressData).forEach(ruleId => {
          const originalProgress = userData.rulesProgress[ruleId];
          if (!originalProgress?.srs_data?.lastReviewedDate) newRulesLearnedCount++;
      });

      setUserData(currentData => {
          const newDocket = JSON.parse(JSON.stringify(currentData.docket || { schedule: [] }));
          if (newRulesLearnedCount > 0) {
              const todayStr = getLocalDateString(new Date());
              let dayEntry = newDocket.schedule.find(d => d.date === todayStr);
              if (dayEntry) {
                  if (!dayEntry.metrics) dayEntry.metrics = { learned_count: 0, quiz_count: 0 };
                  dayEntry.metrics.learned_count += newRulesLearnedCount;
              }
          }
          const finalRulesProgress = { ...currentData.rulesProgress, ...enrichedProgressData };
          const newUserData = { ...currentData, rulesProgress: finalRulesProgress, docket: newDocket };
          
          storage.saveBulkProgress(userId, enrichedProgressData);
          if (newRulesLearnedCount > 0) storage.saveDocket(userId, newDocket);
          return newUserData;
      });
    }

    setDeNovoSession({ state: 'home', queue: [], currentIndex: 0, history: [], elapsedTime: 0, streak: 0, isPaused: false, isRevealed: false, forcedQueue: null });
    setMode('list');
    setSessionType(null);
  }, [userId, userData?.rulesProgress, allCombinedItems, cramDecks]);

  const handleSessionItemComplete = useCallback(async (itemId, score, userAttempt, feedback) => {
    if (!userId) return;

    setUserData(prev => {
      const newRulesProgress = JSON.parse(JSON.stringify(prev.rulesProgress || {}));
      const newQuizzesProgress = JSON.parse(JSON.stringify(prev.quizzesProgress || {}));
      const newDocket = JSON.parse(JSON.stringify(prev.docket || { schedule: [] }));

      const item = allCombinedItems.get(itemId);
      if (!item) return prev;

      const todayStr = getLocalDateString(new Date());
      let todayEntry = newDocket.schedule.find(d => d.date === todayStr);
      if (!todayEntry) {
        todayEntry = { date: todayStr, metrics: { learned_count: 0, quiz_count: 0 }, targets: {}, phases: [] };
        newDocket.schedule.push(todayEntry);
      }
      if (!todayEntry.metrics) todayEntry.metrics = { learned_count: 0, quiz_count: 0 };

      // --- LOGIC FOR RULES ---
      if (sessionType === 'LEARN_NEW_RULES' || sessionType === 'REFINE_HFRS') {
        const rule = item;
        const wasNew = !rule.status || rule.status === 'not_started' || !((prev.rulesProgress || {})[itemId]?.srs_data?.lastReviewedDate);

        if (wasNew) {
          todayEntry.metrics.learned_count = (todayEntry.metrics.learned_count || 0) + 1;
        }

        if (userId) {
              storage.logActivity(userId, 'learn_rule', itemId, 1, {
                  subject: rule.subject,
                  is_hfr: rule.isHFR
              });
              
              // GAMIFICATION: Add points for learning
              storage.addPoints(userId, 10); 
              triggerPointsToast(10);
          }

        const currentSrData = newRulesProgress[itemId]?.srs_data || {};
        const deadline = prev.goals?.target_mastery_date ? new Date(prev.goals.target_mastery_date) : null;
        const newSrData = srsService.calculateNewSRData(currentSrData, 'Good', deadline);

        if (!newRulesProgress[itemId]) newRulesProgress[itemId] = {};
        newRulesProgress[itemId].srs_data = newSrData;
        
        // Save Rule
        storage.saveRuleProgress(userId, itemId, { srs_data: newSrData });

      } 
      // --- LOGIC FOR QUIZZES ---
      else if (sessionType === 'PRACTICE_QUIZZES') {
        todayEntry.metrics.quiz_count = (todayEntry.metrics.quiz_count || 0) + 1;

        // GAMIFICATION: Add points for quiz
        if (userId) {
            storage.addPoints(userId, 5 + (score || 0));
            triggerPointsToast(5 + (score || 0));
        }

        // 1. Update Related Rules History (Linking) - Not saving to DB yet, just local legacy
        const ruleIdsToUpdate = item.related_rule_ids || [];
        ruleIdsToUpdate.forEach(ruleId => {
          const newAttempt = {
            timestamp: new Date().toISOString(),
            userScore: score,
            userAttempt,
            feedback,
            source: 'quiz',
            sourceId: itemId
          };
          const existingData = newRulesProgress[ruleId] || {};
          newRulesProgress[ruleId] = { ...existingData, attempt_history: [newAttempt, ...(existingData.attempt_history || [])] };
        });

        // 2. Save Quiz Attempt to DB (New Table)
        storage.saveQuizAttempt(userId, {
            quiz_id: itemId,
            score,
            user_answer: userAttempt,
            feedback
        });

        // 3. Update Local Quiz State
        const quizAttempt = { timestamp: new Date().toISOString(), userScore: score, userAttempt, feedback };
        const existingQuizData = newQuizzesProgress[itemId] || {};
        const isMastered = score >= 3 || existingQuizData.status === 'mastered';
        
        newQuizzesProgress[itemId] = {
          ...existingQuizData,
          status: isMastered ? 'mastered' : 'in_progress',
          attempt_history: [quizAttempt, ...(existingQuizData.attempt_history || [])],
          last_attempt: new Date().toISOString(),
          high_score: Math.max(score, existingQuizData.high_score || 0)
        };
      }

      // Save Docket
      storage.saveDocket(userId, newDocket);

      return {
        ...prev,
        docket: newDocket,
        rulesProgress: newRulesProgress,
        quizzesProgress: newQuizzesProgress
      };
    });

    startNextInSessionQueue();
}, [sessionType, allCombinedItems, userId, startNextInSessionQueue]);

/* src/App.js - Handlers Block */

  const handleCurriculumConfigSave = useCallback((config) => {
    if (!userId) return;
    lastGoalsUpdateRef.current = Date.now();

    // 1. Prepare Goals Object (Maps to user_goals table)
    const updatedGoals = {
      ...(userData.goals || {}),
      mix_mode: config.mix_mode,
      daily_rule_target: config.daily_rule_target,
      excluded_subjects: config.excluded_subjects,
      hfr_restricted_subjects: config.hfr_restricted_subjects,
      denovo_track_subjects: config.denovo_track_subjects,
      target_mastery_date: config.target_mastery_date,
      study_days: config.study_days
    };

    // 2. Prepare Diagnostics/Preferences (Maps to users table)
    // Note: subject_sequence is stored in user preferences now
    const updatedDiagnostics = {
      ...(userData.diagnostics || {}),
      subject_sequence: config.subject_sequence
    };

    const newFullUserData = {
      ...userData,
      goals: updatedGoals,
      diagnostics: updatedDiagnostics
    };

    // 3. Update Local State
    setUserData(newFullUserData);

    // 4. Save to DB using the Relational Dispatcher
    storage.saveUserData(userId, {
      goals: updatedGoals,
      diagnostics: updatedDiagnostics
    });

    // 5. Trigger Re-calc (Updates local state + saves new docket to DB)
    recalculateCurriculum(newFullUserData);

  }, [userData, userId, recalculateCurriculum]);

  const handlePlanUpdate = useCallback((newPlanSettings) => {
    lastGoalsUpdateRef.current = Date.now();

    // 1. Prepare Goals
    const updatedGoals = {
      ...(userData.goals || {}),
      target_mastery_date: newPlanSettings.targetMasteryDate,
      study_days: newPlanSettings.studyDays || [],
      daily_rule_target: newPlanSettings.daily_rule_target
    };
    
    // 2. Prepare Sequence
    const updatedDiagnostics = { ...(userData.diagnostics || {}), subject_sequence: newPlanSettings.subjectSequence };
    
    const newFullUserData = { ...userData, goals: updatedGoals, diagnostics: updatedDiagnostics };

    // 3. Local Update
    setUserData(newFullUserData);

    // 4. DB Update
    storage.saveUserData(userId, {
        goals: updatedGoals,
        diagnostics: updatedDiagnostics
    });

    // 5. Recalculate Docket
    recalculateCurriculum(newFullUserData);
  }, [userData, userId, recalculateCurriculum]);

  const handleToggleBreakDay = useCallback((dateStr) => {
    if (!userId) return;
    lastDocketUpdateRef.current = Date.now();

    setUserData(prev => {
      const newDocket = JSON.parse(JSON.stringify(prev.docket || { schedule: [] }));
      const existingDayIndex = newDocket.schedule.findIndex(d => d.date === dateStr);

      const dateObj = new Date(dateStr + 'T00:00:00');
      const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const studyDays = prev.goals?.study_days || [];
      const isNormallyStudyDay = studyDays.includes(dayName);

      let newIsBreakStatus;
      if (existingDayIndex > -1) {
        newIsBreakStatus = !newDocket.schedule[existingDayIndex].is_break_day;
      } else {
        // If not in schedule yet, toggle based on whether it's normally a study day
        newIsBreakStatus = isNormallyStudyDay ? true : false;
      }

      // We update the day, but we MUST preserve existing metrics if they exist
      if (existingDayIndex > -1) {
        newDocket.schedule[existingDayIndex].is_break_day = newIsBreakStatus;
      } else {
        newDocket.schedule.push({
          date: dateStr,
          is_break_day: newIsBreakStatus,
          metrics: { learned_count: 0, quiz_count: 0 },
          phases: []
        });
      }

      const updatedUserData = { ...prev, docket: newDocket };

      // 1. GRANULAR SAVE: Only update the docket table
      storage.saveDocket(userId, newDocket);

      // 2. Recalculate: This function is already optimized to use saveDocket internally too
      setTimeout(() => recalculateCurriculum(updatedUserData), 10);

      return updatedUserData;
    });
  }, [userId, recalculateCurriculum]);

  const openFeedbackModal = useCallback((ruleId = null) => {
    setFeedbackRuleId(ruleId);
    setIsFeedbackModalOpen(true);
  }, []);
  const closeFeedbackModal = useCallback(() => setIsFeedbackModalOpen(false), []);
  const openUpgradeModal = useCallback(() => setIsUpgradeModalOpen(true), []);
  const closeUpgradeModal = useCallback(() => setIsUpgradeModalOpen(false), []);

const settingsForPage = useMemo(() => ({
    name: userData?.name || session?.user?.user_metadata?.full_name || '',
    phone: userData?.phone || '',
    defaultTheme: userProfile.preferences?.default_theme,
    apiKey: llmSettings.apiKey,
    barExamDate: userGoals.bar_exam_date,
    targetMasteryDate: userGoals.target_mastery_date,
    goals: userGoals,
    diagnostics: userData?.diagnostics,
    email: session?.user?.email,
    joinedAt: session?.user?.created_at
  }), [userData, userProfile, userGoals, llmSettings, session]);

  const handleSaveSettings = useCallback(async (settingsToSave) => {
    if (!userId || !userData) return;

    lastProfileUpdateRef.current = Date.now();
    
    // Prepare a flat object for the database update
    const dbUpdates = {};
    if (settingsToSave.name !== undefined) dbUpdates.name = settingsToSave.name;
    if (settingsToSave.phone !== undefined) dbUpdates.phone = settingsToSave.phone;
    if (settingsToSave.display_name !== undefined) dbUpdates.display_name = settingsToSave.display_name;
    if (settingsToSave.preferences !== undefined) dbUpdates.preferences = settingsToSave.preferences;
    if (settingsToSave.profile?.zen_mode !== undefined) dbUpdates.zen_mode = settingsToSave.profile.zen_mode;

    // Execute DB update if there are changes
    if (Object.keys(dbUpdates).length > 0) {
        await storage.updateUserProfile(userId, dbUpdates);
    }

    // Optimistically update local state to match
    setUserData(prev => {
        const newProfile = { ...(prev.profile || {}) };
        if (settingsToSave.name !== undefined) newProfile.name = settingsToSave.name;
        if (settingsToSave.phone !== undefined) newProfile.phone = settingsToSave.phone;
        if (settingsToSave.display_name !== undefined) newProfile.display_name = settingsToSave.display_name;
        if (settingsToSave.preferences !== undefined) newProfile.preferences = { ...(newProfile.preferences || {}), ...settingsToSave.preferences };
        if (settingsToSave.profile?.zen_mode !== undefined) newProfile.zen_mode = settingsToSave.profile.zen_mode;
        
        return { ...prev, profile: newProfile };
    });
  }, [userId, userData]);

  const handleExitDemoMode = useCallback(async (skipConfirm = false) => {
  if (!userId) return;
  if (!skipConfirm && !window.confirm("This will clear the demo data and start your real study plan from Day 1. Are you sure?")) return;

  // Reset onboarding status in the database to force re-setup
  await storage.updateOnboardingStatus(userId, 'initiated');

  const emptyState = {
      rulesProgress: {},
      quizzesProgress: {},
      userStats: {},
      docket: { schedule: [] },
      userMeta: { 
          ...userData?.userMeta, 
          is_demo_active: false, 
          onboarding_complete: false,
          current_streak_days: 0,
          last_completed_day: null
      }
  };

  // Save the empty app_data blob
  await storage.saveUserData(userId, emptyState);

  // Update local state to trigger re-route
  setUserData(prev => ({ 
      ...prev, 
      ...emptyState, 
      onboarding_status: 'initiated' 
  }));
  setUserMeta(emptyState.userMeta);
  setAppState('setup');
}, [userId, userData?.userMeta]);

const handleRestartOnboarding = useCallback(async () => {
    if (!userId) return;
    if (window.confirm("This will take you back to the setup screens to adjust your plan. Your learning history will be saved. Continue?")) {
        // 1. Update DB status to re-trigger the setup flow
        await storage.updateOnboardingStatus(userId, 'initiated');

        // 2. Update local state to immediately show the setup view
        setUserData(prev => ({ ...prev, onboarding_status: 'initiated' }));
        setAppState('setup');
    }
  }, [userId]);


  const handleOpenHistoryModal = useCallback((ruleId) => {
    const rule = allCombinedItems.get(ruleId);
    if (rule) {
        setHistoryModalRule(rule);

        // NEW LOGIC: Just Log it. No local meta update needed.
        if (userId) {
            storage.logActivity(userId, 'view_rule', ruleId, 1, {
                subject: rule.subject
            });
        }
    }
}, [allCombinedItems, userId]);

  const handleToggleFlag = useCallback((ruleId) => {
    if (!userId) return;

    setUserData(prev => {
      const newRulesProgress = JSON.parse(JSON.stringify(prev.rulesProgress || {}));
      
      // Ensure object exists
      if (!newRulesProgress[ruleId]) newRulesProgress[ruleId] = {};
      
      // Toggle
      const newFlagValue = !newRulesProgress[ruleId].is_flagged;
      newRulesProgress[ruleId].is_flagged = newFlagValue;

      // GRANULAR SAVE: Update only this specific rule row
      storage.saveRuleProgress(userId, ruleId, { is_flagged: newFlagValue });

      return { ...prev, rulesProgress: newRulesProgress };
    });
  }, [userId]);

  const handleStartCustomDeNovoSession = useCallback((payload) => {
    // The modal now calculates the specific queue array to ensure precise ordering (New -> Review)
    // We check if the payload contains an explicit queue.
    let queue = [];

    if (payload.queue && Array.isArray(payload.queue)) {
      queue = payload.queue;
    } else {
      // Fallback for legacy calls (though the new modal replaces this)
      queue = srsService.buildSessionQueue(allItemsBySubject, userData.rulesProgress, payload, userAccessLevel, userData.goals);
    }

    if (queue.length === 0) {
      alert("No cards found matching your criteria.");
      return;
    }

    setDeNovoSession({
      state: 'active',
      queue,
      currentIndex: 0,
      history: [],
      elapsedTime: 0,
      streak: 0,
      isPaused: false,
      isRevealed: false,
      forcedQueue: null
    });
    setSessionType('CUSTOM_DE_NOVO');
  }, [allItemsBySubject, userData?.rulesProgress, userAccessLevel, userData?.goals]);

  const handleStartCramDeck = useCallback((deckOrId) => {
    let deck;
    if (typeof deckOrId === 'string') {
      deck = cramDecks.find(d => d.id === deckOrId);
    } else {
      deck = deckOrId;
    }

    if (!deck) {
      console.error("Attempted to start a cram session for a deck that could not be found.");
      return;
    }
    
    const completed = new Set(deck.progress?.completed_ids || []);
    const queue = deck.rule_ids.filter(id => !completed.has(id));

    if (queue.length === 0) {
      alert("Deck complete! Reset it from the De Novo home screen to study again.");
      return;
    }

    setDeNovoSession({
      state: 'active', queue, currentIndex: 0, history: [], elapsedTime: 0,
      streak: 0, isPaused: false, isRevealed: false, cramDeckId: deck.id // Tag the session
    });
    setSessionType('CRAM');
  }, [cramDecks]);

    const handleCreateCramDeck = useCallback(async (deckName, ruleIds) => {
    if (!userId || !deckName.trim() || ruleIds.length === 0) return;
    const newDeck = await storage.createCramDeck(userId, deckName.trim(), ruleIds);
    if (newDeck) {
      setCramDecks(prev => [...prev, newDeck]);
      // Immediately start the session with the complete new deck object, bypassing the stale state issue.
      handleStartCramDeck(newDeck);
    }
  }, [userId, handleStartCramDeck]);

  const handleDeleteCramDeck = useCallback(async (deckId) => {
    if (!userId || !window.confirm("Are you sure you want to permanently delete this deck?")) return;
    await storage.deleteCramDeck(deckId);
    setCramDecks(prev => prev.filter(d => d.id !== deckId));
  }, [userId]);

  const handleResetCramDeck = useCallback(async (deckId) => {
    if (!userId) return;
    await storage.resetCramDeck(deckId);
    setCramDecks(prev => prev.map(d => d.id === deckId ? { ...d, progress: {}, last_studied_at: null } : d));
  }, [userId]);

  const handleRenameCramDeck = useCallback(async (deckId, newName) => {
    if (!userId || !newName.trim()) return;
    await storage.renameCramDeck(deckId, newName.trim());
    setCramDecks(prev => prev.map(d => d.id === deckId ? { ...d, deck_name: newName.trim() } : d));
  }, [userId]);

const handleSetupComplete = useCallback(async (configUpdates) => {
    if (!userId) return;

    const profileData = {
        name: (configUpdates.profile.name || '').trim(),
        phone: (configUpdates.profile.phone || '').trim()
    };

    await storage.updateOnboardingStatus(userId, 'completed');
    await storage.updateUserProfile(userId, profileData);

    await storage.sendNotification(
        userId,
        'system',
        'Welcome to Wali\'s!',
        "Your personalized docket is ready. The bar exam is a volume game, and you just started winning."
    );

    let newUserData = {
      ...userData,
      name: profileData.name,
      phone: profileData.phone,
      goals: { ...(userData.goals || {}), ...configUpdates.goals },
      diagnostics: { ...(userData.diagnostics || {}), ...configUpdates.diagnostics },
      userMeta: { ...(userData.userMeta || {}), onboarding_complete: true },
      onboarding_status: 'completed'
    };

    // INJECT DEMO DATA FOR FREE USERS
    if (newUserData.access_level === 'free') {
        newUserData = {
            ...newUserData,
            rulesProgress: { ...newUserData.rulesProgress, ...DEMO_STATE.rulesProgress },
            userStats: { ...newUserData.userStats, ...DEMO_STATE.userStats },
            userMeta: { ...newUserData.userMeta, is_demo_active: true }
        };
        // Explicitly save the demo progress to DB immediately
        await storage.saveBulkProgress(userId, DEMO_STATE.rulesProgress);
    }

    const { schedule, error } = generateCurriculum({ userData: newUserData, canonicalData });

    if (error) { console.error("Plan Gen Error", error); }

    const finalUserData = { ...newUserData, docket: { schedule } };

    await storage.saveDocket(userId, finalUserData.docket);
    handleUpdateUserData(finalUserData);
    setUserMeta(finalUserData.userMeta);
    setAppState('ready'); 
  }, [canonicalData, handleUpdateUserData, userData, userId]);

  const handleSlidesComplete = () => {
    setAppState('ready');
    setTimeout(() => setShowTutorial(true), 500);
  };

  useEffect(() => {
    if (!userId || !todaysDocketEntry || userData?.profile?.zen_mode) return;

    const checkStreakRisk = () => {
        const now = new Date();
        const hour = now.getHours();
        
        // Only warn between 6 PM and 11 PM
        if (hour >= 18 && hour <= 23) {
            const learned = todaysDocketEntry.metrics?.learned_count || 0;
            const target = todaysDocketEntry.targets?.learn || 5;
            
            // If they haven't finished learning...
            if (learned < target) {
                const todayStr = getLocalDateString(now);
                const localKey = `warn_streak_${userId}_${todayStr}`;
                const hasWarned = localStorage.getItem(localKey);

                if (!hasWarned) {
                    storage.sendNotification(
                        userId, 
                        'streak', 
                        'Streak Risk', 
                        `It's getting late. Finish learning ${target - learned} more rules to save your streak.`
                    );
                    localStorage.setItem(localKey, 'true');
                    
                    // Trigger visual toast
                    if (triggerPointsToast) {
                        triggerPointsToast("Streak Risk: Check Inbox");
                    }
                }
            }
        }
    };

    // Check on mount and every 15 minutes
    checkStreakRisk();
    const interval = setInterval(checkStreakRisk, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userId, todaysDocketEntry, userData?.profile?.zen_mode]);

  const renderModals = () => (
    <>

      {historyModalRule && (
        <HistoryModal
          rule={historyModalRule}
          onClose={() => setHistoryModalRule(null)}
          allRules={[...allCombinedItems.values()]}
          onUpdateRule={handleRuleUpdate}
          onDeleteRule={() => { /* logic for delete if needed */ }}
          onToggleFlag={handleToggleFlag}
          onReportIssue={openFeedbackModal} 
        />
      )}

      <FeedbackModal isOpen={isFeedbackModalOpen} onClose={closeFeedbackModal} ruleId={feedbackRuleId} />
      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
      <UpgradeModal isOpen={isUpgradeModalOpen} onClose={closeUpgradeModal} />
      <DeNovoSetupModal
        isVisible={isDeNovoSetupModalOpen}
        onClose={() => setIsDeNovoSetupModalOpen(false)}
        onStart={(settings) => { setIsDeNovoSetupModalOpen(false); handleStartCustomDeNovoSession(settings); }}
        subjectMap={subjectMapDerived}
        userAccessLevel={userAccessLevel}
        allItemsMap={allCombinedItems}
        userGoals={userGoals}
        rulesProgress={userData?.rulesProgress}
        onCreateCramDeck={handleCreateCramDeck}
      />
      <CurriculumConfigModal
        isOpen={isCurriculumConfigModalOpen}
        onClose={() => setIsCurriculumConfigModalOpen(false)}
        subjectMap={subjectMapDerived}
        userData={userData}
        allItemsMap={allCombinedItems} // Ensure this is passed
        onSaveConfiguration={handleCurriculumConfigSave}
      />
    </>
  );



  const viewNamesMap = { dashboard: 'Dashboard', library: 'Library', planner: 'Planner', corpus: 'Corpus Juris', deNovo: 'De Novo', settings: 'Settings' };
  const availableViews = ['dashboard', 'planner', 'corpus', 'deNovo', 'settings'];

  const onStartChapterLearn = useCallback(() => {
    const queue = (allItemsBySubject[subject.toLowerCase()] || [])
      .filter(r => r.itemType === 'rule' && r.status === 'not_started')
      .map(r => r.id);
    if (queue.length === 0) { alert("No new rules to learn in this chapter! Try 'Refine' or 'Challenge' instead."); return; }

    setSessionType('LEARN_NEW_RULES');
    setSessionQueue(queue);
    setSessionProgress({ current: 0, total: queue.length });
    setCurrentRule(allCombinedItems.get(queue[0]));
    setMode('learn');
  }, [allItemsBySubject, subject, allCombinedItems]);


  const onStartChapterRefine = useCallback(() => {
    const queue = (allItemsBySubject[subject.toLowerCase()] || [])
      .filter(r => r.itemType === 'rule' && r.status === 'in_progress')
      .map(r => r.id);
    if (queue.length === 0) { alert("No rules are ready for refinement yet. Learn some new rules first!"); return; }

    setSessionType('REFINE_CHAPTER');
    setSessionQueue(queue);
    setSessionProgress({ current: 0, total: queue.length });
    setCurrentRule(allCombinedItems.get(queue[0]));
    setMode('refine');
  }, [allItemsBySubject, subject, allCombinedItems]);


  const onStartChapterFlagged = useCallback(() => {
    const queue = (allItemsBySubject[subject.toLowerCase()] || [])
      .filter(r => r.isFlagged)
      .map(r => r.id);
    if (queue.length === 0) { alert("You haven't flagged any rules in this chapter yet."); return; }

    setSessionType('REFINE_CHAPTER');
    setSessionQueue(queue);
    setSessionProgress({ current: 0, total: queue.length });
    setCurrentRule(allCombinedItems.get(queue[0]));
    setMode('refine');
  }, [allItemsBySubject, subject, allCombinedItems]);


  const onStartChapterChallenge = useCallback(() => {
    const queue = (allItemsBySubject[subject.toLowerCase()] || [])
      .filter(r => r.itemType === 'quiz')
      .map(r => r.id);
    if (queue.length === 0) { alert("No quizzes available for this chapter yet."); return; }

    setSessionType('PRACTICE_QUIZZES');
    setSessionQueue(queue);
    setSessionProgress({ current: 0, total: queue.length });
    setCurrentQuiz(allCombinedItems.get(queue[0]));
    setMode('test');
  }, [allItemsBySubject, subject, allCombinedItems]);


  const onStartGlobalFlaggedReview = useCallback(() => {
    const queue = [...allCombinedItems.values()]
      .filter(r => r.isFlagged)
      .map(r => r.id);
    if (queue.length === 0) { alert("Your flagged list is empty!"); return; }

    setSessionType('REFINE_CHAPTER');
    setSessionQueue(queue);
    setSessionProgress({ current: 0, total: queue.length });
    setCurrentRule(allCombinedItems.get(queue[0]));
    setMode('refine');
  }, [allCombinedItems]);

  const subjectsForPage = useMemo(() => Object.entries(subjectMap).map(([id, name]) => ({ subject_id: id, name })), []);

  const availableQuizCount = useMemo(() => {
    if (!allCombinedItems || allCombinedItems.size === 0) return 0;
    let count = 0;
    for (const item of allCombinedItems.values()) {
      if (item.itemType === 'quiz') {
        // Filter by Access Level
        if (userAccessLevel === 'free' && !item.is_free_preview) continue;

        // Exclude Mastered (consistent with session start logic)
        if (item.status === 'mastered') continue;

        // Check if prerequisites (Rules) are learned
        const parents = item.related_rule_ids || [];
        if (parents.length > 0) {
          const parentsLearned = parents.every(rid => {
            const r = allCombinedItems.get(rid);
            return r && r.status !== 'not_started';
          });
          if (parentsLearned) count++;
        }
      }
    }
    return count;
  }, [allCombinedItems, userAccessLevel]);

const handleRuleUpdate = useCallback((updatedRule) => {
    if (!userId) return;

    // 1. Prepare Data for DB
    const updatePayload = {
        keynote: updatedRule.keynote
    };
    if (updatedRule.isFlagged !== undefined) {
        updatePayload.is_flagged = updatedRule.isFlagged;
    }

    // 2. Granular DB Update
    storage.saveRuleProgress(userId, updatedRule.id, updatePayload);

    // 3. Update Local State
    setUserData(prev => {
      const newRulesProgress = { ...prev.rulesProgress };
      if (!newRulesProgress[updatedRule.id]) newRulesProgress[updatedRule.id] = {};

      newRulesProgress[updatedRule.id].keynote = updatedRule.keynote;
      if (updatedRule.isFlagged !== undefined) {
        newRulesProgress[updatedRule.id].is_flagged = updatedRule.isFlagged;
      }
      
      // Keep custom_data for local session (optional, depending on if we persist content edits)
      newRulesProgress[updatedRule.id].custom_data = {
        ruleName: updatedRule.ruleName,
        content_html: updatedRule.content_html,
        content_plain: updatedRule.content_plain,
        displayPath: updatedRule.displayPath
      };

      return { ...prev, rulesProgress: newRulesProgress };
    });

    // Update local modal ref
    setHistoryModalRule(prev => ({ ...prev, ...updatedRule }));
  }, [userId]);

  // --- REVISED AUTH CHECK FOR SEPARATE DOMAIN ---
  if (!session) {
    return <LoginScreen onLoginSuccess={() => { }} />;
  }

  if (appState === 'loading' || !subject || !userData || canonicalData.rules.length === 0) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '1.5em', fontFamily: 'var(--font-heading)', backgroundColor: 'var(--bg-color-light)' }}>Loading Wali's... ⏳</div>;
  }

  if (appState === 'setup') {
    return (
      <SetupView onSetupComplete={handleSetupComplete} subjects={subjectsForPage} userData={userData} />
    );
  }

  if (appState === 'onboarding_slides') {
    return <InteractiveOnboarding onComplete={handleSlidesComplete} />;
  }



  return (
  <div className={`app-shell ${userMeta?.is_demo_active ? 'has-demo-banner' : ''}`}>
    {userMeta?.is_demo_active && (
      <div className="demo-mode-banner">
        <div className="demo-content">
          <span className="material-symbols-outlined">science</span>
          <span>
            <strong>Simulator Mode:</strong> You are viewing a sample plan. 
            <em>Note: Your setup preferences are saved but overridden by the Torts Free Preview curriculum until you upgrade.</em>
          </span>
        </div>
        <div className="demo-actions">
          <button className="demo-upgrade-btn" onClick={openUpgradeModal}>
            <span className="material-symbols-outlined">workspace_premium</span>
            Unlock Full Access
          </button>
        </div>
      </div>
    )}

    {renderModals()}

    {(mode !== 'list' && deNovoSession.state !== 'active') && (
      <div className="mode-overlay-wrapper">
        {(mode === 'learn' || mode === 'refine') && currentRule && (
          <LearnModeScreen rule={currentRule} onComplete={handleSessionItemComplete} onBack={handleBackToList} apiPort={apiPort} llmSettings={llmSettings} onEditRule={handleOpenHistoryModal} onToggleFlag={handleToggleFlag} onUpdateRuleKeynote={handleUpdateRuleKeynote} sessionMode={mode} sessionProgress={sessionProgress} seenHints={userData?.userMeta?.seen_hints} onDismissHint={handleDismissHint} />
        )}
        {mode === 'test' && currentQuiz && (<TestModeScreen quiz={currentQuiz} onComplete={handleSessionItemComplete} onBack={handleBackToList} apiPort={apiPort} llmSettings={llmSettings} sessionProgress={sessionProgress} onOpenHistoryModal={handleOpenHistoryModal} />)}
        {mode === 'batch_summary' && (<BatchSummaryScreen completedRules={completedBatchItems} allItemsMap={allCombinedItems} dailyProgress={todaysDocketEntry?.metrics?.learned_count || 0} dailyTarget={todaysDocketEntry?.targets?.learn || 0} onContinue={handleContinueNextBatch} onFinish={handleBackToList} />)}
      </div>
    )}

    {(mode === 'list' || deNovoSession.state === 'active') && (
      <>
        {deNovoSession.state === 'active' ? (
          <DeNovoView allRulesBySubject={allItemsBySubject} deNovoData={userData?.rulesProgress || {}} onSessionEnd={handleDeNovoSessionEnd} onToggleFlag={handleToggleFlag} userSettings={{ targetMasteryDate: userGoals.target_mastery_date, barExamDate: userGoals.bar_exam_date }} onEditRule={handleOpenHistoryModal} subjectMap={subjectMapDerived} sessionState={deNovoSession} sessionType={sessionType} onUpdateSessionState={(updates) => setDeNovoSession(prev => ({ ...prev, ...updates }))} todaysDocketEntry={todaysDocketEntry} onStartPhase={handleStartDynamicSession} onStartCustomSession={(settings) => handleStartCustomDeNovoSession(settings)} onCreateCustomSession={() => setIsDeNovoSetupModalOpen(true)} activityLog={userData?.activityLog || []} onCardUpdate={handleCardUpdate} userAccessLevel={userAccessLevel} dailyDueCount={reviewQueueStats.reviewCount + reviewQueueStats.fastTrackCount} seenHints={userData?.userMeta?.seen_hints} onDismissHint={handleDismissHint} userId={userId} cramDecks={cramDecks} onStartCramDeck={handleStartCramDeck} onDeleteCramDeck={handleDeleteCramDeck} onResetCramDeck={handleResetCramDeck} onRenameCramDeck={handleRenameCramDeck} onTriggerToast={triggerPointsToast} />
        ) : (
          <>
            <header className={`app-header ${!headerVisible ? 'header-hidden' : ''}`}>
              <div className={`app-title-container fixed-header-element ${isScrolled ? 'header-glass-effect' : ''}`}>
                <img src="/logo.png" alt="Wali's" className="app-icon" style={{ borderRadius: '10px' }} />
                <h1>Wali's</h1>
              </div>
<nav className={`glass-pill-nav fixed-header-element ${isScrolled ? 'collapsed' : ''}`}>
                {!userData?.profile?.zen_mode && (
                    <button onClick={() => setView('arena')} className={view === 'arena' ? 'active' : ''}>
                        <span className="material-symbols-outlined">swords</span>
                        <span className="nav-label">Arena</span>
                    </button>
                )}
                <button onClick={() => setView('dashboard')} className={view === 'dashboard' ? 'active' : ''}><span className="material-symbols-outlined">dashboard</span><span className="nav-label">Dashboard</span></button>
                <button onClick={() => setView('planner')} className={view === 'planner' ? 'active' : ''}><span className="material-symbols-outlined">calendar_month</span><span className="nav-label">Planner</span></button>
                <button onClick={() => setView('corpus')} className={view === 'corpus' ? 'active' : ''}><span className="material-symbols-outlined">book_ribbon</span><span className="nav-label">Corpus</span></button>
                <button onClick={() => setView('deNovo')} className={view === 'deNovo' ? 'active' : ''}><span className="material-symbols-outlined">stacks</span><span className="nav-label">De Novo</span></button>
              </nav>
              <div className={`header-controls fixed-header-element ${isScrolled ? 'header-glass-effect' : ''}`}>
                <NotificationDropdown userId={userId} zenMode={userData?.profile?.zen_mode} />
                <button className="header-icon-btn" onClick={handleToggleDarkMode} title="Toggle Theme"><span className="material-symbols-outlined theme-icon">{isDarkMode ? 'light_mode' : 'dark_mode'}</span></button>
                    <button 
                      className="header-avatar-btn header-icon-btn" 
                      onClick={() => setView('profile')} 
                      title="Profile & Settings"
                      style={{
                          backgroundColor: (() => {
                              const rules = Object.values(userData?.rulesProgress || {}).filter(p => p.srs_data?.lastReviewedDate);
                              if (rules.length === 0) return 'var(--accent-color-primary)';
                              
                              const counts = rules.reduce((acc, curr) => {
                                  const ruleId = Object.keys(userData.rulesProgress).find(key => userData.rulesProgress[key] === curr);
                                  const rule = allCombinedItems.get(ruleId);
                                  if (rule) acc[rule.subject] = (acc[rule.subject] || 0) + 1;
                                  return acc;
                              }, {});

                              const topSub = Object.entries(counts).sort((a,b) => b[1] - a[1])[0]?.[0];
                              const index = orderedSubjectKeys.indexOf(topSub);
                              return index >= 0 ? SUBJECT_PALETTE[index] : 'var(--accent-color-primary)';
                          })(),
                          color: '#fff',
                          border: '2px solid rgba(255,255,255,0.5)'
                      }}
                  >
                      {(userData?.profile?.display_name || 'C').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                  </button>
              </div>
            </header>
            <div className="app-container">
              <div className="main-content-area">
                <TransitionGroup component={null}>
                  <CSSTransition key={view} nodeRef={nodeRef} timeout={200} classNames="fade">
                    <div className="view-content-wrapper" ref={nodeRef}>
                      {view === 'arena' && (<ArenaView />)}
                      {view === 'dashboard' && (<Dashboard docket={userData?.docket} onStartPhase={handleStartDynamicSession} handleRecalculateCurriculum={handleRecalculateCurriculum} userGoals={userGoals} allItemsMap={allCombinedItems} userAccessLevel={userAccessLevel} rulesProgress={userData?.rulesProgress} setView={setView} userData={userData} reviewCount={reviewQueueStats.reviewCount} fastTrackCount={reviewQueueStats.fastTrackCount} onOpenHistoryModal={handleOpenHistoryModal} onOpenConfig={() => setIsCurriculumConfigModalOpen(true)} onOpenUpgrade={openUpgradeModal} availableQuizCount={availableQuizCount} onOpenContact={() => setIsContactModalOpen(true)} onReportIssue={openFeedbackModal} seenHints={userData?.userMeta?.seen_hints} onDismissHint={handleDismissHint} />)}
                      {view === 'library' && (<LibraryView allSubjectsData={allItemsBySubject} allItemsMap={allCombinedItems} subjectMapDerived={subjectMapDerived} setSubject={setSubject} setView={setView} userAccessLevel={userAccessLevel} onOpenUpgrade={openUpgradeModal} rulesProgress={userData?.rulesProgress} onOpenRule={handleOpenHistoryModal} seenHints={userData?.userMeta?.seen_hints}onDismissHint={handleDismissHint} />)}
                      {view === 'corpus' && (<CorpusJurisView subject={subject} setSubject={setSubject} subjectMapDerived={subjectMapDerived} allSubjectsData={allItemsBySubject} setView={setView} allItemsMap={allCombinedItems} subjectProgress={subjectProgress} chapterModeEligibility={chapterModeEligibility} onOpenHistoryModal={handleOpenHistoryModal} onLearnClick={(id) => { setSessionQueue([id]); setSessionType('LEARN_NEW_RULES'); setSessionProgress({ current: 0, total: 1 }); startNextInSessionQueue([id], 'LEARN_NEW_RULES'); }} onRefineClick={(id) => { setSessionQueue([id]); setSessionType('REFINE_CHAPTER'); setSessionProgress({ current: 0, total: 1 }); startNextInSessionQueue([id], 'REFINE_CHAPTER'); }} onTestClick={(id) => { setSessionQueue([id]); setSessionType('PRACTICE_QUIZZES'); setSessionProgress({ current: 0, total: 1 }); startNextInSessionQueue([id], 'PRACTICE_QUIZZES'); }} onToggleFlag={handleToggleFlag} flaggedForReviewCount={flaggedForReviewCount} onStartChapterLearn={onStartChapterLearn} onStartChapterRefine={onStartChapterRefine} onStartChapterFlagged={onStartChapterFlagged} onStartChapterChallenge={onStartChapterChallenge} onStartGlobalFlaggedReview={onStartGlobalFlaggedReview} onSetIsAddingRule={setIsAddingRule} onReportIssue={openFeedbackModal} userAccessLevel={userAccessLevel} onUpdateRule={handleRuleUpdate}  />)}
                      {view === 'planner' && (<PlannerView docket={userData?.docket} userGoals={userGoals} diagnostics={userData?.diagnostics} subjectMapDerived={subjectMapDerived} allItemsMap={allCombinedItems} onPlanUpdate={handlePlanUpdate} setView={setView} setSubject={setSubject} onToggleBreakDay={handleToggleBreakDay} onStartPhase={handleStartDynamicSession} userAccessLevel={userAccessLevel} userData={userData} onOpenConfig={() => setIsCurriculumConfigModalOpen(true)} seenHints={userData?.userMeta?.seen_hints} onDismissHint={handleDismissHint} />)}
                      {view === 'deNovo' && (<DeNovoView allRulesBySubject={allItemsBySubject} deNovoData={userData?.rulesProgress || {}} onSessionEnd={handleDeNovoSessionEnd} onToggleFlag={handleToggleFlag} userSettings={{ targetMasteryDate: userGoals.target_mastery_date, barExamDate: userGoals.bar_exam_date }} onEditRule={handleOpenHistoryModal} subjectMap={subjectMapDerived} sessionState={deNovoSession} sessionType={sessionType} onUpdateSessionState={(updates) => setDeNovoSession(prev => ({ ...prev, ...updates }))} todaysDocketEntry={todaysDocketEntry} onStartPhase={handleStartDynamicSession} onStartCustomSession={(settings) => handleStartCustomDeNovoSession(settings)} onCreateCustomSession={() => setIsDeNovoSetupModalOpen(true)} activityLog={userData?.activityLog || []} onCardUpdate={handleCardUpdate} userAccessLevel={userAccessLevel} dailyDueCount={reviewQueueStats.reviewCount + reviewQueueStats.fastTrackCount} seenHints={userData?.userMeta?.seen_hints} onDismissHint={handleDismissHint} userId={userId} cramDecks={cramDecks} onStartCramDeck={handleStartCramDeck} onDeleteCramDeck={handleDeleteCramDeck} onResetCramDeck={handleResetCramDeck} onRenameCramDeck={handleRenameCramDeck} onTriggerToast={triggerPointsToast} />)}
                      {view === 'profile' && (<ProfileView userData={{ ...userData, achievements: userData?.achievements || [], topSubject: Object.values(userData?.rulesProgress || {}) .filter(p => p.srs_data?.lastReviewedDate) .reduce((acc, curr, _, arr) => { const rule = allCombinedItems.get(Object.keys(userData.rulesProgress).find(key => userData.rulesProgress[key] === curr)); if (rule) acc[rule.subject] = (acc[rule.subject] || 0) + 1; return acc; }, {}) }} onSaveSettings={handleSaveSettings} onRestartOnboarding={handleRestartOnboarding} onStartTutorial={() => setShowTutorial(true)} onOpenUpgrade={openUpgradeModal} setView={setView} onOpenContact={() => setIsContactModalOpen(true)} />)}
                    </div>
                  </CSSTransition>
                </TransitionGroup>
              </div>
              <footer className="app-footer">
                <div className="footer-content">
                  <div className="footer-top">
                    <div className="footer-nav">
                      <a href="https://walis.app/privacy" target="_blank" rel="noreferrer" className="footer-link">Privacy Policy</a>
                      <a href="https://walis.app/terms" target="_blank" rel="noreferrer" className="footer-link">Terms of Service</a>
                      <a href="#" className="footer-link" onClick={(e) => { e.preventDefault(); setView('settings'); }}>System Status</a>
                    </div>
                    <button className="btn" style={{ display: 'flex' }} onClick={() => openFeedbackModal(null)}><span className="material-symbols-outlined" style={{ fontSize: '1.2em' }}>bug_report</span>Report Issue</button>
                  </div>
                  <div className="footer-bottom">
                    <div className="copyright-text"><span className="footer-logo-text">Wali's Bar Prep</span> &copy; {new Date().getFullYear()}. All Rights Reserved.</div>
                    <div className="version-info"><span className="app-version">v6.1.1-stable</span></div>
                  </div>
                </div>
              </footer>
            </div>
          </>
        )}
      </>
    )}
    {pointsToast && (
        <div className={`points-toast fade-in ${pointsToast.type === 'badge' ? 'badge-toast' : ''}`}>
            <span className="material-symbols-outlined">
                {pointsToast.type === 'badge' ? 'military_tech' : 'add_circle'}
            </span>
            <span>
                {pointsToast.type === 'badge' 
                    ? `UNLOCKED: ${pointsToast.name}` 
                    : typeof pointsToast === 'string' 
                        ? pointsToast // Render the custom string from DeNovoView
                        : `+${pointsToast} ARENA POINTS` // Fallback for legacy numeric calls
                }
            </span>
        </div>
    )}
  </div>
);
}

export default App;